import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BsBoxArrowUpRight, BsBrush, BsChatDots, BsCollection, BsDownload, BsFiles, BsLink45Deg, BsPlus } from 'react-icons/bs'
import { getOptionsConfig, getTypeName, parseDataset } from '@rawgraphs/rawgraphs-core'
import { tsvParse } from 'd3-dsv'
import baseCharts from '../charts'
import ChartPreviewWithOptions from '../components/ChartPreviewWithOptions'
import { normalizeJsonArray, parseAndCheckData } from '../hooks/useDataLoaderUtils/parser'
import { DEFAULT_BRAND_THEME, buildBrandColorScaleOption } from '../services/brandTheme'
import { buildDimensionMappingForColumn } from '../services/chartDimensionMapping'
import { createChartSnapshot, getCompatibleChartVisualOptions, getDefaultChartVisualOptions } from '../services/chartSnapshot'
import { AGGREGATIONS_LABELS } from '../constants'
import { runDataAudit } from '../services/dataAudit'
import CanvasVisualControls from './CanvasVisualControls'
import CanvasLayoutDiagnostics from './CanvasLayoutDiagnostics'
import CanvasLayoutActions from './CanvasLayoutActions'
import CanvasStyleControls from './CanvasStyleControls'
import CanvasDataExplorer from './CanvasDataExplorer'
import { CANVAS_STORAGE_KEY, canvasWorkspaceNeedsAsyncLoad, openCanvasChartInLugsail, readCanvasWorkspace, saveCanvasWorkspace } from './canvasWorkspace'
import { buildCompatibleChartMapping, getCompatibleCanvasCharts } from './canvasCompatibility'
import { createCanvasFrame, getFrameMoveIds, removeObjectsFromFrames } from './canvasFrames'
import { downloadCanvasChartSvg } from './canvasSvgExport'
import { createCanvasGenerationSnapshot, getCanvasStyleSource } from './canvasPresentation'
import { applyCanvasThemeToSelection } from './canvasStyleTransfer'
import styles from './CanvasLab.module.scss'

const SAMPLE_URL = '/sample-datasets/Line chart - RIAA Music format revenues.tsv'
const RATIOS = { '16:9': 16 / 9, '4:3': 4 / 3, '1:1': 1, '4:5': 4 / 5, '9:16': 9 / 16 }
const CANVAS_ADVISORY_SAMPLE_SIZE = 600
const CANVAS_AUDIT_SAMPLE_SIZE = 2000
const makeId = (kind) => `${kind}-${Math.random().toString(36).slice(2, 10)}`
const createDatasetLoader = () => ({ id: 'dataset-loader', kind: 'dataset', x: 120, y: 150, w: 260, h: 236 })
const emptyCanvasWorkspace = () => ({ dataset: null, objects: [createDatasetLoader()], camera: { x: 0, y: 0, zoom: 1 } })
// The shared preview hooks observe these callbacks. Keeping Canvas no-ops
// stable prevents a spatial move/resize from looking like a chart-data update.
const NOOP = () => {}
const SVG_PREVIEW_MODE = { mode: 'svg' }
function sanitizeCanvasVisualOptions(chart, visualOptions = {}) {
  const defaults = getDefaultChartVisualOptions(chart)
  return Object.fromEntries(Object.entries(visualOptions).map(([key, value]) => [
    key,
    typeof value === 'number' && Math.abs(value) > 100000 ? defaults[key] : value,
  ]))
}

function applyCanvasArtboard(chart, visualOptions, aspectRatio) {
  const defaults = getDefaultChartVisualOptions(chart)
  const width = 1200
  const height = Math.round(width / (RATIOS[aspectRatio] || RATIOS['16:9']))
  return {
    ...visualOptions,
    ...(Object.prototype.hasOwnProperty.call(defaults, 'width') ? { width } : {}),
    ...(Object.prototype.hasOwnProperty.call(defaults, 'height') ? { height } : {}),
  }
}

// Dates become ISO strings when a local Canvas workspace is serialized. The
// RAWGraphs parser has already inferred their format, so restore Date objects
// directly rather than asking it to infer a second, incompatible ISO format.
function hydratePersistedDateColumns(dataset) {
  const dateColumns = Object.keys(dataset?.dataTypes || {}).filter(
    (column) => getTypeName(dataset.dataTypes[column]) === 'date'
  )
  if (!dateColumns.length) return dataset

  let changed = false
  const rows = (dataset.rows || []).map((row) => {
    let nextRow = row
    dateColumns.forEach((column) => {
      const value = row[column]
      if (value == null || value instanceof Date) return
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return
      if (nextRow === row) nextRow = { ...row }
      nextRow[column] = date
      changed = true
    })
    return nextRow
  })
  return changed ? { ...dataset, rows } : dataset
}

// Canvas exposes two primary mapping controls. Additional mappings must be
// explicitly supported by the inspector (currently the optional legend field).
// Keeping the primary rule in one place prevents hidden mappings changing a
// chart that the Canvas cannot explain or edit.
function getCanvasDimensions(chart) {
  if (chart?.metadata?.id === 'rawgraphs.forcedirectedgraph') return chart.dimensions || []
  return (chart?.dimensions || []).slice(0, 2)
}

function getCompatibleDimensionColumns(dimension, dataTypes = {}) {
  return Object.keys(dataTypes).filter((column) => (
    !dimension.validTypes?.length
    || dimension.validTypes.includes(getTypeName(dataTypes[column]))
  ))
}

function buildMultipleDimensionMapping(previousMapping, dimension, columns, dataTypes) {
  const previousValues = previousMapping?.value || []
  const previousAggregations = previousMapping?.config?.aggregation || []
  const aggregationByColumn = new Map(
    previousValues.map((column, index) => [column, previousAggregations[index]])
  )
  const mapping = columns.reduce(
    (current, column) => buildDimensionMappingForColumn(current, dimension, column, dataTypes),
    {}
  )
  if (dimension.aggregation && mapping.config?.aggregation) {
    mapping.config.aggregation = mapping.config.aggregation.map(
      (aggregation, index) => aggregationByColumn.get(columns[index]) || aggregation
    )
  }
  return mapping
}

// Legends use a chart-specific mapping: usually `color`, but `group` on some
// charts. Read that relationship from the production option schema instead of
// assuming a dimension name in Canvas.
function getLegendDimension(chart) {
  const dimensionId = getOptionsConfig(chart?.visualOptions)?.colorScale?.dimension
  return chart?.dimensions?.find((dimension) => dimension.id === dimensionId)
}

function getCanvasBrandColorScale(theme, chart, mapping, dataset) {
  const dimension = getLegendDimension(chart)
  const columns = dimension && mapping?.[dimension.id]?.value
  if (!columns?.length) return null

  // Multi-set and stacked bars color their measure fields themselves. Their
  // renderer calls colorScale with the field names (not row values), so the
  // palette domain must be those names too.
  if (dimension.multiple) {
    return buildBrandColorScaleOption(theme, columns.map(String))
  }

  const column = columns[0]
  if (!dataset?.rows?.length) return null

  const domain = Array.from(new Set(
    dataset.rows
      .map((row) => row[column])
      .filter((value) => value !== null && value !== undefined && value !== '')
      .map(String)
  ))
  return domain.length ? buildBrandColorScaleOption(theme, domain) : null
}

function hasSeparateLegendMapping(chart) {
  const dimension = getLegendDimension(chart)
  return Boolean(
    dimension && !getCanvasDimensions(chart).some((item) => item.id === dimension.id)
  )
}

// RAWGraphs' bar chart has one bar per Bars value. Its optional Color field is
// aggregated for that bar, rather than creating grouped or stacked bars. A
// field with several values for the same bar therefore resolves to a combined
// value (for example, "North, South") that cannot match an individual palette
// entry. Only offer one-to-one fields there, so this lightweight Canvas
// shortcut genuinely colors bars and never changes the bar calculation.
function getCanvasLegendColumns(chart, dataset, mapping) {
  const dimension = getLegendDimension(chart)
  if (!dimension || !dataset) return []
  const columns = Object.keys(dataset.dataTypes || {}).filter((column) => (
    !dimension.validTypes?.length
    || dimension.validTypes.includes(getTypeName(dataset.dataTypes[column]))
  ))
  if (chart?.metadata?.id !== 'rawgraphs.barchart') return columns

  const barsColumn = mapping?.bars?.value?.[0]
  if (!barsColumn) return columns
  return columns.filter((column) => {
    const valuesByBar = new Map()
    for (const row of dataset.rows || []) {
      const bar = row[barsColumn]
      const value = row[column]
      if (bar == null || value == null || value === '') continue
      const key = String(bar)
      const values = valuesByBar.get(key) || new Set()
      values.add(String(value))
      if (values.size > 1) return false
      valuesByBar.set(key, values)
    }
    return true
  })
}

function getVariantBaseTitle(title, fallback) {
  return (title || fallback).replace(/\s+\d+$/, '')
}

function getNextChartTitle(objects, baseTitle) {
  const usedTitles = new Set(objects.filter((object) => object.kind === 'chart').map((object) => object.title))
  if (!usedTitles.has(baseTitle)) return baseTitle
  let number = 2
  while (usedTitles.has(`${baseTitle} ${number}`)) number += 1
  return `${baseTitle} ${number}`
}

function defaultMapping(chart, dataTypes, rows = []) {
  const fields = Object.keys(dataTypes || {}).map((name) => ({
    name,
    type: getTypeName(dataTypes[name]),
    uniqueValues: new Set(rows.slice(0, 2000).map((row) => row[name])).size,
  }))
  const compatible = (dimension) => fields.filter((field) => !dimension.validTypes?.length || dimension.validTypes.includes(field.type))
  const pick = (dimension) => {
    const candidates = compatible(dimension)
    const numeric = candidates.filter((field) => field.type === 'number')
    const categorical = candidates.filter((field) => field.type !== 'number')
    if (dimension.aggregation) {
      return [...numeric].sort((a, b) => {
        const score = (field) => {
          const name = field.name.toLowerCase()
          if (/(rating|score|value|duration|revenue|amount|count)/.test(name)) return 0
          if (/(year|date|time|latitude|longitude|\bid\b)/.test(name)) return 2
          return 1
        }
        return score(a) - score(b)
      })[0] || candidates[0]
    }
    if (chart?.metadata?.id === 'rawgraphs.linechart') {
      return candidates.find((field) => field.type === 'date') || numeric[0] || categorical[0] || candidates[0]
    }
    // Bar-like charts need a discrete field. Prefer a small number of categories
    // so a random dataset does not create hundreds of illegible bars.
    return categorical.find((field) => field.uniqueValues > 1 && field.uniqueValues <= 40) || categorical[0] || numeric.find((field) => field.uniqueValues <= 40) || candidates[0]
  }
  return getCanvasDimensions(chart).reduce((out, dimension) => {
    const fieldsToMap = chart?.metadata?.id === 'rawgraphs.forcedirectedgraph' && dimension.id === 'steps'
      ? []
      : dimension.multiple
      ? compatible(dimension).filter((field) => field.type === 'number').slice(0, 2)
      : [pick(dimension)].filter(Boolean)
    if (!fieldsToMap.length) return out
    return {
      ...out,
      [dimension.id]: fieldsToMap.reduce(
        (current, field) => buildDimensionMappingForColumn(current, dimension, field.name, dataTypes),
        out[dimension.id] || {}
      ),
    }
  }, {})
}

// Pasted API payloads are often an object that wraps records (`data`,
// `results`, etc.), while a single JSON object is also a perfectly valid
// one-row dataset. Accept both without creating a second ingestion path.
function rowsFromCanvasJson(value) {
  if (Array.isArray(value)) return value
  if (!value || typeof value !== 'object') return null

  const commonRecordKeys = ['data', 'results', 'records', 'items', 'value', 'features']
  for (const key of commonRecordKeys) {
    if (Array.isArray(value[key])) return value[key]
  }

  const nestedRecords = Object.values(value).find(
    (child) => Array.isArray(child) && child.some((item) => item && typeof item === 'object')
  )
  return nestedRecords || [value]
}

function CanvasChart({ spec, dataset, onVisualOptionsChange, onLayoutDiagnostics }) {
  const chart = baseCharts.find((candidate) => candidate.metadata.id === spec.chartId)
  // The card does not add a second title/header. The production SVG owns all
  // chart text, so Canvas and normal Lugsail display the same composition.
  const brandTheme = useMemo(() => ({
    ...DEFAULT_BRAND_THEME,
    ...(spec.brandTheme || {}),
    title: spec.brandTheme?.title || spec.title || chart?.metadata?.name || 'Chart',
  }), [chart?.metadata?.name, spec.brandTheme, spec.title])
  const colorScale = useMemo(
    () => getCanvasBrandColorScale(brandTheme, chart, spec.mapping, dataset),
    [brandTheme, chart, spec.mapping, dataset]
  )
  const visualOptions = useMemo(() => colorScale
    ? { ...spec.visualOptions, colorScale: { ...spec.visualOptions?.colorScale, ...colorScale } }
    : spec.visualOptions,
  [spec.visualOptions, colorScale])
  if (!chart || !dataset) return null
  return <div className={styles.chartRender}><ChartPreviewWithOptions chart={chart} dataset={dataset.rows} dataTypes={dataset.dataTypes} mapping={spec.mapping} visualOptions={visualOptions} setVisualOptions={onVisualOptionsChange} setRawViz={NOOP} setMappingLoading={NOOP} brandTheme={brandTheme} showOptions={false} previewRendererMode={SVG_PREVIEW_MODE} previewClassName="col-12" renderSource="labs-canvas" enableAutoMargin={false} onLayoutDiagnostics={onLayoutDiagnostics} /></div>
}

// Rendering a production SVG can be expensive. Keep title typing local and
// commit after a short pause (or immediately on blur) so the inspector never
// waits for a complete chart redraw on each keystroke.
function CanvasChartTitleInput({ value, onCommit }) {
  const [draft, setDraft] = useState(value)
  const timer = useRef(null)

  useEffect(() => {
    window.clearTimeout(timer.current)
    setDraft(value)
  }, [value])
  useEffect(() => () => window.clearTimeout(timer.current), [])
  const commit = useCallback((nextValue) => {
    window.clearTimeout(timer.current)
    onCommit(nextValue)
  }, [onCommit])
  const change = useCallback((event) => {
    const nextValue = event.target.value
    setDraft(nextValue)
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => commit(nextValue), 220)
  }, [commit])

  return <input value={draft} onChange={change} onBlur={() => commit(draft)} onKeyDown={(event) => { event.stopPropagation(); if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur() } }} />
}

/** A route-local canvas surface: intentionally narrow, with no production dependencies. */
export default function CanvasLab() {
  const [state, setState] = useState(() => { try { const saved = localStorage.getItem(CANVAS_STORAGE_KEY); return saved && saved !== '__indexeddb__' ? JSON.parse(saved) : emptyCanvasWorkspace() } catch { return emptyCanvasWorkspace() } })
  const [workspaceHydrated, setWorkspaceHydrated] = useState(() => !canvasWorkspaceNeedsAsyncLoad())
  const [selectedIds, setSelectedIds] = useState([])
  const [dataDialogOpen, setDataDialogOpen] = useState(false)
  const [dataPreviewOpen, setDataPreviewOpen] = useState(false)
  const [pendingDataset, setPendingDataset] = useState(null)
  const [pasteInput, setPasteInput] = useState('')
  const [loadError, setLoadError] = useState(null)
  const [loadingData, setLoadingData] = useState(false)
  const [layoutDiagnostics, setLayoutDiagnostics] = useState({})
  const [inspectorTab, setInspectorTab] = useState('map')
  const [inspectorCollapsed, setInspectorCollapsed] = useState(false)
  const gesture = useRef(null)
  const canvasRef = useRef(null)
  const worldRef = useRef(null)
  const zoomRef = useRef(null)
  const cameraRef = useRef(state.camera || { x: 0, y: 0, zoom: 1 })
  const cameraFrameRef = useRef(null)
  const zoomCommitRef = useRef(null)
  const latestWorkspaceRef = useRef(state)

  // Panning and zooming are visual-only until a gesture settles. Updating a
  // DOM transform in an animation frame keeps all chart components out of the
  // interaction loop.
  const paintCamera = useCallback((camera) => {
    cameraRef.current = camera
    if (cameraFrameRef.current) return
    cameraFrameRef.current = window.requestAnimationFrame(() => {
      cameraFrameRef.current = null
      if (worldRef.current) {
        const { x, y, zoom } = cameraRef.current
        worldRef.current.style.transform = `translate(${x}px, ${y}px) scale(${zoom})`
      }
    })
  }, [])
  const commitCamera = useCallback(() => {
    setState((current) => ({ ...current, camera: { ...cameraRef.current } }))
  }, [])
  const resetWorkspace = useCallback(() => {
    const nextWorkspace = emptyCanvasWorkspace()
    if (zoomCommitRef.current) window.clearTimeout(zoomCommitRef.current)
    zoomCommitRef.current = null
    latestWorkspaceRef.current = nextWorkspace
    saveCanvasWorkspace(nextWorkspace).catch(() => {})
    paintCamera(nextWorkspace.camera)
    setState(nextWorkspace)
    setSelectedIds([])
    setDataDialogOpen(false)
    setPasteInput('')
    setLoadError(null)
  }, [paintCamera])

  useEffect(() => {
    paintCamera(cameraRef.current)
    return () => {
      if (cameraFrameRef.current) window.cancelAnimationFrame(cameraFrameRef.current)
      if (zoomCommitRef.current) window.clearTimeout(zoomCommitRef.current)
    }
  }, [paintCamera])
  useEffect(() => {
    if (workspaceHydrated) return undefined
    let active = true
    readCanvasWorkspace().then((workspace) => {
      if (active && workspace) {
        latestWorkspaceRef.current = workspace
        cameraRef.current = workspace.camera || { x: 0, y: 0, zoom: 1 }
        setState(workspace)
      }
    }).catch(() => {}).finally(() => { if (active) setWorkspaceHydrated(true) })
    return () => { active = false }
  }, [workspaceHydrated])
  // Workspace positions can change many times per second while dragging.
  // Save the final settled state instead of serializing the full dataset on
  // every pointer event.
  useEffect(() => {
    latestWorkspaceRef.current = state
    if (!workspaceHydrated) return undefined
    const timer = window.setTimeout(() => { saveCanvasWorkspace(latestWorkspaceRef.current).catch(() => {}) }, 450)
    return () => window.clearTimeout(timer)
  }, [state, workspaceHydrated])
  useEffect(() => () => {
    if (workspaceHydrated) saveCanvasWorkspace(latestWorkspaceRef.current).catch(() => {})
  }, [workspaceHydrated])
  // Production previews receive parsed rows. Upgrade old workspaces and
  // rehydrate Date objects lost during localStorage serialization.
  useEffect(() => {
    setState((current) => {
      const hasDatasetLoader = current.objects.some((object) => object.kind === 'dataset')
      const objects = hasDatasetLoader
        ? current.objects.map((object) => object.kind === 'dataset' && object.h < 236
          ? { ...object, h: 236 }
          : object)
        : [createDatasetLoader(), ...current.objects]
      if (!current.dataset) {
        return objects === current.objects ? current : { ...current, objects }
      }
      if (current.dataset.isParsed) {
        const dataset = hydratePersistedDateColumns(current.dataset)
        return dataset === current.dataset && objects === current.objects
          ? current
          : { ...current, objects, dataset }
      }
      const parsed = parseDataset(current.dataset.rows, current.dataset.dataTypes, {})
      return { ...current, objects, dataset: { ...current.dataset, rows: parsed.dataset, dataTypes: parsed.dataTypes, isParsed: true } }
    })
  }, [])
  // Upgrade workspaces created before chart snapshots carried full Lugsail
  // defaults. This only fills missing fields and leaves existing variants alone.
  useEffect(() => {
    setState((current) => {
      let changed = false
      const objects = current.objects.map((object) => {
        if (object.kind !== 'chart' || (object.visualOptions && object.brandTheme)) return object
        const chart = baseCharts.find((candidate) => candidate.metadata.id === object.chartId)
        if (!chart) return object
        changed = true
        return {
          ...object,
          ...createChartSnapshot({
            chart,
            mapping: object.mapping,
            visualOptions: object.visualOptions,
            brandTheme: object.brandTheme || DEFAULT_BRAND_THEME,
          }),
        }
      })
      return changed ? { ...current, objects } : current
    })
  }, [])
  useEffect(() => {
    setState((current) => {
      let changed = false
      const objects = current.objects.map((object) => {
        if (object.kind !== 'chart' || !current.dataset) return object
        const chart = baseCharts.find((candidate) => candidate.metadata.id === object.chartId)
        if (!chart) return object
        let mapping = object.mapping || {}
        const mappingDimensions = getCanvasDimensions(chart).concat(
          getLegendDimension(chart) ? [getLegendDimension(chart)] : []
        )
        mappingDimensions.forEach((dimension) => {
          const column = mapping[dimension.id]?.value?.[0]
          if (!column || !dimension.aggregation || mapping[dimension.id]?.config?.aggregation) return
          mapping = {
            ...mapping,
            [dimension.id]: buildDimensionMappingForColumn(
              mapping[dimension.id],
              dimension,
              column,
              current.dataset.dataTypes
            ),
          }
          changed = true
        })
        return mapping === object.mapping ? object : { ...object, mapping }
      })
      return changed ? { ...current, objects } : current
    })
  }, [])
  useEffect(() => {
    setState((current) => {
      let changed = false
      const objects = current.objects.map((object) => {
        if (object.kind !== 'chart') return object
        const chart = baseCharts.find((candidate) => candidate.metadata.id === object.chartId)
        if (!chart) return object
        const visualOptions = applyCanvasArtboard(
          chart,
          sanitizeCanvasVisualOptions(chart, object.visualOptions),
          object.aspectRatio || '16:9'
        )
        if (Object.keys(visualOptions).every((key) => visualOptions[key] === object.visualOptions?.[key])) return object
        changed = true
        return { ...object, visualOptions }
      })
      return changed ? { ...current, objects } : current
    })
  }, [])
  const selected = state.objects.find((object) => object.id === selectedIds[0])
  const selectedChart = selected?.kind === 'chart' ? baseCharts.find((chart) => chart.metadata.id === selected.chartId) : null
  const selectedBumpSizeDimension = selectedChart?.metadata?.id === 'rawgraphs.bumpchart'
    ? selectedChart.dimensions.find((dimension) => dimension.id === 'size')
    : null
  const selectedChartNeedsNumericSize = Boolean(
    selectedBumpSizeDimension
    && !getCompatibleDimensionColumns(selectedBumpSizeDimension, state.dataset?.dataTypes).length
  )
  const selectedChartIds = selectedIds.filter(
    (id) => state.objects.find((object) => object.id === id)?.kind === 'chart'
  )
  const canvasChartCount = state.objects.filter((object) => object.kind === 'chart').length
  const selectedLayoutDiagnostics = selected?.kind === 'chart' ? layoutDiagnostics[selected.id] : null
  const selectedLegendColumns = useMemo(
    () => {
      const columns = getCanvasLegendColumns(selectedChart, state.dataset, selected?.mapping)
      const current = getLegendDimension(selectedChart) && selected?.mapping?.[getLegendDimension(selectedChart).id]?.value?.[0]
      return current && !columns.includes(current) ? [current, ...columns] : columns
    },
    [selectedChart, state.dataset, selected?.mapping]
  )
  const datasetColumns = useMemo(
    () => Object.keys(state.dataset?.dataTypes || state.dataset?.rows?.[0] || {}),
    [state.dataset]
  )
  const dataAudit = useMemo(
    () => {
      if (!state.dataset) return null
      const rows = state.dataset.rows || []
      const auditRows = rows.length > CANVAS_AUDIT_SAMPLE_SIZE
        ? rows.slice(0, CANVAS_AUDIT_SAMPLE_SIZE)
        : rows
      return runDataAudit(auditRows, { totalRows: rows.length, sampled: auditRows.length !== rows.length })
    },
    [state.dataset]
  )
  const dataHealthSummary = useMemo(() => {
    if (!dataAudit) return null
    const missingValues = Object.values(dataAudit.schema).reduce(
      (total, column) => total + (column.missingCount || 0),
      0
    )
    const typeCounts = Object.values(dataAudit.schema).reduce((counts, column) => ({
      ...counts,
      [column.type]: (counts[column.type] || 0) + 1,
    }), {})
    const types = Object.entries(typeCounts)
      .map(([type, count]) => `${count} ${type}`)
      .join(' · ')
    return { missingValues, types, duplicateRows: dataAudit.duplicateRows, sampled: dataAudit.sampled }
  }, [dataAudit])
  const compatibleCanvasCharts = useMemo(
    () => state.dataset
      ? getCompatibleCanvasCharts(
        baseCharts,
        state.dataset.dataTypes,
        state.dataset.rows.length > CANVAS_ADVISORY_SAMPLE_SIZE
          ? state.dataset.rows.slice(0, CANVAS_ADVISORY_SAMPLE_SIZE)
          : state.dataset.rows,
        6
      )
      : [],
    [state.dataset]
  )
  const selectableCanvasCharts = useMemo(() => {
    const charts = compatibleCanvasCharts.map(({ chart }) => chart)
    return selectedChart && !charts.some((chart) => chart.metadata.id === selectedChart.metadata.id)
      ? [selectedChart, ...charts]
      : charts
  }, [compatibleCanvasCharts, selectedChart])

  const commitDataset = useCallback((dataset) => {
    setState((current) => ({
      ...current,
      dataset,
      objects: [{
        ...(current.objects.find((object) => object.kind === 'dataset') || createDatasetLoader()),
        h: Math.max(current.objects.find((object) => object.kind === 'dataset')?.h || 0, 236),
      }],
    }))
    setSelectedIds([])
    setDataDialogOpen(false)
    setPasteInput('')
    setLoadError(null)
  }, [])
  const addDataset = useCallback((dataset) => {
    if (state.dataset) {
      setPendingDataset(dataset)
      return false
    }
    commitDataset(dataset)
    return true
  }, [commitDataset, state.dataset])
  const cancelDatasetReplacement = useCallback(() => setPendingDataset(null), [])
  const confirmDatasetReplacement = useCallback(() => {
    if (!pendingDataset) return
    commitDataset(pendingDataset)
    setPendingDataset(null)
  }, [commitDataset, pendingDataset])
  const parseCanvasDataset = useCallback((input, name) => {
    const [dataType, parsedInput, parseError] = parseAndCheckData(input, {})
    if (parseError) throw new Error(parseError)
    const rows = dataType === 'json'
      ? normalizeJsonArray(rowsFromCanvasJson(parsedInput) || [])
      : parsedInput
    if (!rows?.length) throw new Error('Provide CSV, TSV, a JSON array, or a JSON object with at least one row.')
    const parsed = parseDataset(rows, undefined, {})
    if (!parsed?.dataTypes) throw new Error('Lugsail could not infer the columns in this dataset.')
    return { id: makeId('dataset'), name: name || 'Untitled dataset', rows: parsed.dataset, dataTypes: parsed.dataTypes, isParsed: true }
  }, [])
  const loadTextDataset = useCallback(async (input, name) => {
    setLoadingData(true)
    setLoadError(null)
    try {
      const dataset = parseCanvasDataset(input, name)
      addDataset(dataset)
    } catch (error) {
      setLoadError(error.message || 'Lugsail could not load that data.')
    } finally {
      setLoadingData(false)
    }
  }, [addDataset, parseCanvasDataset])
  const loadSample = useCallback(async () => {
    setLoadingData(true)
    setLoadError(null)
    try {
      const response = await fetch(SAMPLE_URL)
      if (!response.ok) throw new Error('The sample dataset could not be loaded.')
      const rows = tsvParse(await response.text())
      const parsed = parseDataset(rows, undefined, {})
      addDataset({ id: makeId('dataset'), name: 'RIAA Music Format Revenues', rows: parsed.dataset, dataTypes: parsed.dataTypes, isParsed: true })
    } catch (error) {
      setLoadError(error.message || 'The sample dataset could not be loaded.')
    } finally {
      setLoadingData(false)
    }
  }, [addDataset])
  const handleFile = useCallback(async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    await loadTextDataset(await file.text(), file.name.replace(/\.[^.]+$/, ''))
    event.target.value = ''
  }, [loadTextDataset])
  const addChart = useCallback((chartId = 'rawgraphs.barchart') => {
    const chart = baseCharts.find((candidate) => candidate.metadata.id === chartId)
    if (!state.dataset || !chart) return
    const aspectRatio = '16:9'
    setState((current) => {
      const snapshot = createCanvasGenerationSnapshot({ chart, mapping: defaultMapping(chart, current.dataset.dataTypes, current.dataset.rows), styleSource: getCanvasStyleSource(current.objects, selectedIds) })
      const title = getNextChartTitle(current.objects, chart.metadata.name)
      return { ...current, objects: [...current.objects, { id: makeId('chart'), kind: 'chart', title, x: 460, y: 130, w: 420, h: 420 / RATIOS[aspectRatio], datasetId: current.dataset.id, ...snapshot, visualOptions: applyCanvasArtboard(chart, snapshot.visualOptions, aspectRatio), aspectRatio }] }
    })
  }, [selectedIds, state.dataset])
  const openDataPreview = useCallback(() => {
    setDataPreviewOpen(true)
  }, [])
  const generateCompatibleCharts = useCallback(() => {
    if (!state.dataset) return
    setState((current) => {
      const datasetObject = current.objects.find((object) => object.kind === 'dataset')
      const origin = datasetObject || { x: 120, y: 150, w: 260, h: 148 }
      const candidates = getCompatibleCanvasCharts(
        baseCharts,
        current.dataset.dataTypes,
        current.dataset.rows,
        6
      )
      if (!candidates.length) return current

      const cardWidth = 360
      const aspectRatio = '16:9'
      const columns = 3
      const lowestExistingEdge = current.objects.reduce(
        (lowest, object) => Math.max(lowest, object.y + object.h),
        origin.y + origin.h
      )
      const startX = Math.max(80, origin.x)
      const startY = lowestExistingEdge + 80
      const charts = candidates.map(({ chart, mapping }, index) => {
        const snapshot = createCanvasGenerationSnapshot({ chart, mapping, styleSource: getCanvasStyleSource(current.objects, selectedIds) })
        const column = index % columns
        const row = Math.floor(index / columns)
        return {
          id: makeId('chart'),
          kind: 'chart',
          title: getNextChartTitle(current.objects, chart.metadata.name),
          x: startX + column * (cardWidth + 44),
          y: startY + row * (cardWidth / RATIOS[aspectRatio] + 54),
          w: cardWidth,
          h: cardWidth / RATIOS[aspectRatio],
          datasetId: current.dataset.id,
          ...snapshot,
          visualOptions: applyCanvasArtboard(chart, snapshot.visualOptions, aspectRatio),
          aspectRatio,
        }
      })
      return { ...current, objects: [...current.objects, ...charts] }
    })
  }, [selectedIds, state.dataset])
  const duplicate = useCallback(() => {
    if (selected?.kind !== 'chart') return
    const copyId = makeId('chart')
    setState((current) => {
      const chart = baseCharts.find((candidate) => candidate.metadata.id === selected.chartId)
      const baseTitle = getVariantBaseTitle(selected.title, chart?.metadata?.name || 'Chart')
      const copy = { ...selected, id: copyId, title: getNextChartTitle(current.objects, baseTitle), x: selected.x + 36, y: selected.y + 36, parentVersionId: selected.id }
      return { ...current, objects: [...current.objects, copy] }
    })
    setSelectedIds([copyId])
  }, [selected])
  const createFrame = useCallback(() => {
    const childIds = selectedIds.filter((id) => state.objects.find((object) => object.id === id)?.kind === 'chart')
    if (childIds.length < 2) return
    const frame = createCanvasFrame(state.objects, childIds, makeId('frame'))
    if (!frame) return
    setState((current) => ({ ...current, objects: [...current.objects, frame] }))
    setSelectedIds([frame.id])
  }, [selectedIds, state.objects])
  const updateSelected = useCallback((patch) => setState((current) => ({ ...current, objects: current.objects.map((object) => object.id === selected?.id ? { ...object, ...patch } : object) })), [selected])
  const updateChartVisualOptions = useCallback((id, nextOptions) => {
    setState((current) => ({
      ...current,
      objects: current.objects.map((object) => {
        if (object.id !== id) return object
        const visualOptions = typeof nextOptions === 'function'
          ? nextOptions(object.visualOptions)
          : nextOptions
        return { ...object, visualOptions }
      }),
    }))
  }, [])
  const updateChartLayoutDiagnostics = useCallback((id, nextDiagnostics) => {
    setLayoutDiagnostics((current) => {
      const previous = current[id]
      if (JSON.stringify(previous) === JSON.stringify(nextDiagnostics)) return current
      return { ...current, [id]: nextDiagnostics }
    })
  }, [])
  const updateSelectedBrandTheme = useCallback((nextBrandTheme) => {
    if (!selected?.id) return
    const colorScale = getCanvasBrandColorScale(
      nextBrandTheme,
      selectedChart,
      selected.mapping,
      state.dataset
    )
    setState((current) => ({
      ...current,
      objects: current.objects.map((object) => object.id === selected.id
        ? {
            ...object,
            brandTheme: nextBrandTheme,
            visualOptions: colorScale
              ? { ...object.visualOptions, colorScale: { ...object.visualOptions?.colorScale, ...colorScale } }
              : object.visualOptions,
          }
        : object),
    }))
  }, [selected, selectedChart, state.dataset])
  const updateSelectedLegendMapping = useCallback((column) => {
    const dimension = getLegendDimension(selectedChart)
    if (!dimension || !selected) return
    const mapping = { ...selected.mapping }
    if (column) {
      mapping[dimension.id] = buildDimensionMappingForColumn(
        mapping[dimension.id] || {}, dimension, column, state.dataset.dataTypes
      )
    } else {
      delete mapping[dimension.id]
    }
    const colorScale = column
      ? getCanvasBrandColorScale(selected.brandTheme || DEFAULT_BRAND_THEME, selectedChart, mapping, state.dataset)
      : null
    const optionsConfig = getOptionsConfig(selectedChart.visualOptions) || {}
    updateSelected({
      mapping,
      visualOptions: {
        ...selected.visualOptions,
        ...(colorScale ? { colorScale: { ...selected.visualOptions?.colorScale, ...colorScale } } : {}),
        ...(Object.prototype.hasOwnProperty.call(optionsConfig, 'showLegend')
          ? { showLegend: Boolean(column) }
          : {}),
      },
    })
  }, [selected, selectedChart, state.dataset, updateSelected])
  const updateSelectedMultipleDimension = useCallback((dimension, column, checked) => {
    if (!selected) return
    const currentColumns = selected.mapping?.[dimension.id]?.value || []
    const columns = checked
      ? [...new Set([...currentColumns, column])]
      : currentColumns.filter((value) => value !== column)
    // Required multi-value dimensions need at least one field to stay
    // renderable. The checkbox is also disabled at that boundary in the UI.
    if (dimension.required && !columns.length) return
    updateSelected({
      mapping: {
        ...selected.mapping,
        [dimension.id]: buildMultipleDimensionMapping(
          selected.mapping?.[dimension.id], dimension, columns, state.dataset.dataTypes
        ),
      },
    })
  }, [selected, state.dataset, updateSelected])
  const updateSelectedMultipleAggregation = useCallback((dimension, column, aggregation) => {
    if (!selected) return
    const current = selected.mapping?.[dimension.id]
    const index = current?.value?.indexOf(column)
    if (index == null || index < 0) return
    const aggregations = [...(current.config?.aggregation || [])]
    aggregations[index] = aggregation
    updateSelected({
      mapping: {
        ...selected.mapping,
        [dimension.id]: { ...current, config: { ...current.config, aggregation: aggregations } },
      },
    })
  }, [selected, updateSelected])
  const applySelectedStyleToSelection = useCallback(() => {
    if (!selected?.id || selectedChartIds.length < 2) return
    setState((current) => ({
      ...current,
      objects: applyCanvasThemeToSelection(current.objects, selected.id, selectedChartIds),
    }))
  }, [selected?.id, selectedChartIds])

  const beginObjectGesture = (event, object, mode = 'move') => {
    event.stopPropagation()
    const nextSelection = event.shiftKey ? [...new Set([...selectedIds, object.id])] : [object.id]
    setSelectedIds(nextSelection)
    const moveIds = mode === 'move' && object.kind === 'frame'
      ? [...new Set([...nextSelection, ...getFrameMoveIds(object)])]
      : nextSelection
    gesture.current = { mode, startX: event.clientX, startY: event.clientY, objects: state.objects, selection: moveIds, target: object }
    event.currentTarget.setPointerCapture(event.pointerId)
  }
  const moveGesture = (event) => {
    const active = gesture.current
    if (!active || active.mode === 'pan') return
    const dx = (event.clientX - active.startX) / cameraRef.current.zoom
    const dy = (event.clientY - active.startY) / cameraRef.current.zoom
    setState((current) => ({ ...current, objects: current.objects.map((object) => {
      const initial = active.objects.find((item) => item.id === object.id)
      if (!initial || !active.selection.includes(object.id)) return object
      if (active.mode === 'resize') { const ratio = object.kind === 'chart' ? RATIOS[object.aspectRatio] : initial.w / initial.h; const w = Math.max(140, initial.w + dx); return { ...object, w, h: w / ratio } }
      return { ...object, x: initial.x + dx, y: initial.y + dy }
    }) }))
  }
  const endGesture = () => {
    const active = gesture.current
    gesture.current = null
    if (active?.mode === 'pan') commitCamera()
  }
  const beginPan = (event) => {
    const world = event.currentTarget.firstElementChild
    if (event.target !== event.currentTarget && event.target !== world) return
    gesture.current = { mode: 'pan', startX: event.clientX, startY: event.clientY, camera: { ...cameraRef.current } }
    event.currentTarget.setPointerCapture(event.pointerId)
    setSelectedIds([])
  }
  const pan = (event) => {
    const active = gesture.current
    if (!active || active.mode !== 'pan') return
    paintCamera({
      ...active.camera,
      x: active.camera.x + event.clientX - active.startX,
      y: active.camera.y + event.clientY - active.startY,
    })
  }
  const zoom = (event) => {
    event.preventDefault()
    const multiplier = event.deltaY > 0 ? .9 : 1.1
    const bounds = event.currentTarget.getBoundingClientRect()
    const currentCamera = cameraRef.current
    const previousZoom = currentCamera.zoom
    const nextZoom = Math.max(.25, Math.min(2.5, previousZoom * multiplier))
    const selectedObject = state.objects.find((object) => selectedIds.includes(object.id))
    const anchorX = selectedObject
      ? selectedObject.x + selectedObject.w / 2
      : (event.clientX - bounds.left - currentCamera.x) / previousZoom
    const anchorY = selectedObject
      ? selectedObject.y + selectedObject.h / 2
      : (event.clientY - bounds.top - currentCamera.y) / previousZoom
    const screenX = selectedObject ? bounds.width / 2 : event.clientX - bounds.left
    const screenY = selectedObject ? bounds.height / 2 : event.clientY - bounds.top
    paintCamera({
      x: screenX - anchorX * nextZoom,
      y: screenY - anchorY * nextZoom,
      zoom: nextZoom,
    })
    if (zoomCommitRef.current) window.clearTimeout(zoomCommitRef.current)
    zoomCommitRef.current = window.setTimeout(() => {
      zoomCommitRef.current = null
      commitCamera()
    }, 180)
  }
  zoomRef.current = zoom
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return undefined

    // A trackpad pinch is delivered as a ctrl-wheel event by browsers. Register
    // natively with passive:false so cancelling it prevents browser/page zoom;
    // only the board's world transform should change scale.
    const handleWheel = (event) => zoomRef.current?.(event)
    canvas.addEventListener('wheel', handleWheel, { passive: false })
    return () => canvas.removeEventListener('wheel', handleWheel)
  }, [])
  useEffect(() => {
    const keydown = (event) => {
      const target = event.target
      const editingText = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target?.isContentEditable
      if (editingText) return
      if (event.key === 'Delete' || event.key === 'Backspace') {
        setState((current) => {
          const removableIds = selectedIds.filter(
            (id) => current.objects.find((object) => object.id === id)?.kind !== 'dataset'
          )
          return { ...current, objects: removeObjectsFromFrames(current.objects, removableIds) }
        })
        setSelectedIds([])
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'd') {
        event.preventDefault()
        duplicate()
      }
    }
    window.addEventListener('keydown', keydown)
    return () => window.removeEventListener('keydown', keydown)
  }, [duplicate, selectedIds])

  return <main className={styles.lab}>
    <header className={styles.header}><div><b>Lugsail Labs</b><span>/ Canvas</span></div><button onClick={resetWorkspace}>Reset</button><a href="/">Return to Lugsail</a></header>
    {dataDialogOpen && <div className={styles.dataDialogBackdrop} role="presentation" onMouseDown={() => setDataDialogOpen(false)}><section className={styles.dataDialog} role="dialog" aria-modal="true" aria-labelledby="canvas-data-title" onMouseDown={(event) => event.stopPropagation()}><div className={styles.dataDialogHeader}><div><h2 id="canvas-data-title">{state.dataset ? 'Replace canvas data' : 'Add data to canvas'}</h2><p>Uses the same browser-side parsing and type inference as Lugsail. Your file stays on this device.</p></div><button type="button" onClick={() => setDataDialogOpen(false)} aria-label="Close">×</button></div><div className={styles.dataChoices}><label className={styles.fileChoice}>Upload CSV, TSV, or JSON<input type="file" accept=".csv,.tsv,.txt,.json,text/csv,text/plain,application/json,text/tab-separated-values" onChange={handleFile} /></label><button type="button" className={styles.sampleChoice} onClick={loadSample} disabled={loadingData}>Use RIAA music-revenue sample</button></div><label className={styles.pasteLabel}>Or paste CSV, TSV, a JSON array, or a JSON object<textarea value={pasteInput} onChange={(event) => setPasteInput(event.target.value)} placeholder={'category,value\\nA,18\\nB,31'} /></label><button type="button" className={styles.primaryAction} disabled={loadingData || !pasteInput.trim()} onClick={() => loadTextDataset(pasteInput, 'Pasted data')}>{loadingData ? 'Loading…' : 'Add pasted data'}</button>{loadError && <p className={styles.loadError}>{loadError}</p>}</section></div>}
    {pendingDataset && <div className={styles.dataDialogBackdrop} role="presentation" onMouseDown={cancelDatasetReplacement}><section className={styles.replaceDataConfirm} role="dialog" aria-modal="true" aria-labelledby="canvas-replace-data-title" onMouseDown={(event) => event.stopPropagation()}><h2 id="canvas-replace-data-title">Replace this canvas dataset?</h2><p><b>{pendingDataset.name}</b> is ready to load. {canvasChartCount ? `Your ${canvasChartCount === 1 ? 'chart' : `${canvasChartCount} charts`} will be cleared because their field mappings belong to the current data.` : 'The current dataset will be replaced.'}</p><p className={styles.replaceDataNote}>This only changes the Canvas workspace. Your file stays on this device.</p><div><button type="button" onClick={cancelDatasetReplacement}>Keep current data</button><button type="button" onClick={confirmDatasetReplacement}>Replace data</button></div></section></div>}
    {dataPreviewOpen && state.dataset && <div className={styles.dataDialogBackdrop} role="presentation" onMouseDown={() => setDataPreviewOpen(false)}><section className={`${styles.dataDialog} ${styles.dataPreviewDialog}`} role="dialog" aria-modal="true" aria-labelledby="canvas-data-preview-title" onMouseDown={(event) => event.stopPropagation()}><div className={styles.dataDialogHeader}><div><h2 id="canvas-data-preview-title">{state.dataset.name}</h2><p>{state.dataset.rows.length.toLocaleString()} rows · {datasetColumns.length} columns · read-only explorer</p></div><button type="button" onClick={() => setDataPreviewOpen(false)} aria-label="Close data explorer">×</button></div><CanvasDataExplorer dataset={state.dataset} columns={datasetColumns} audit={dataAudit} /></section></div>}
    <div className={styles.workspace}>
      <aside className={styles.rail}><button onClick={() => addChart()} disabled={!state.dataset}><BsPlus aria-hidden="true" />Add chart</button><button onClick={duplicate} disabled={selected?.kind !== 'chart'}><BsFiles aria-hidden="true" />Duplicate</button><button onClick={createFrame} disabled={selectedIds.filter((id) => state.objects.find((object) => object.id === id)?.kind === 'chart').length < 2}><BsCollection aria-hidden="true" />Frame selection</button>{selectedChart && <div className={styles.railActions}><button type="button" onClick={() => openCanvasChartInLugsail({ chart: selectedChart, dataset: state.dataset, spec: selected })}><BsBoxArrowUpRight aria-hidden="true" />Open in Lugsail</button><button type="button" onClick={() => downloadCanvasChartSvg(selected.id, selected.title)}><BsDownload aria-hidden="true" />Download SVG</button></div>}<p>Drag objects. Drag empty canvas to pan. Scroll to zoom a selected chart. Shift-click for multi-select.</p></aside>
      <section ref={canvasRef} className={styles.canvas} onPointerDown={beginPan} onPointerMove={(event) => { pan(event); moveGesture(event) }} onPointerUp={endGesture}>
        <div className={styles.world} ref={worldRef}>
          {[...state.objects.filter((object) => object.kind === 'frame'), ...state.objects.filter((object) => object.kind !== 'frame')].map((object) => <div key={object.id} data-canvas-chart-id={object.kind === 'chart' ? object.id : undefined} className={`${styles.object} ${object.kind === 'frame' ? styles.frameObject : ''} ${selectedIds.includes(object.id) ? styles.objectSelected : ''}`} style={{ left: object.x, top: object.y, width: object.w, height: object.h }} onPointerDown={(event) => beginObjectGesture(event, object)}>
            {object.kind === 'frame' ? <div className={styles.frameCard}><b>{object.name || 'Chart iterations'}</b><span>{(object.childIds || []).length} charts</span></div> : object.kind === 'dataset' ? <div className={styles.datasetCard}><b>Dataset loader</b><span>{state.dataset ? <>{state.dataset.name} · {state.dataset.rows.length} rows · {Object.keys(state.dataset.dataTypes || {}).length} columns<br />{dataHealthSummary?.types} · {dataHealthSummary?.missingValues || 0}{dataHealthSummary?.sampled ? ' sample' : ''} blanks{dataHealthSummary?.duplicateRows ? ` · ${dataHealthSummary.duplicateRows} duplicates` : ''}</> : 'Load a CSV, TSV, or JSON dataset to begin.'}</span><div className={styles.datasetActions}><button type="button" className={styles.exploreButton} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); setLoadError(null); setDataDialogOpen(true) }}>Load/Replace data</button><button type="button" className={styles.exploreButton} disabled={!state.dataset} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); openDataPreview() }}>Explore</button><button type="button" className={styles.compatibleButton} disabled={!state.dataset} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); generateCompatibleCharts() }}>Generate charts</button></div></div> : <div className={styles.chartCard} style={{ backgroundColor: object.brandTheme?.backgroundColor || object.visualOptions?.background || '#fff' }}><CanvasChart spec={object} dataset={state.dataset} onVisualOptionsChange={(nextOptions) => updateChartVisualOptions(object.id, nextOptions)} onLayoutDiagnostics={(diagnostics) => updateChartLayoutDiagnostics(object.id, diagnostics)} /></div>}
            {selectedIds.includes(object.id) && <span className={styles.resizeHandle} onPointerDown={(event) => beginObjectGesture(event, object, 'resize')} />}
          </div>)}
        </div>
      </section>
      <aside className={`${styles.inspector} ${inspectorCollapsed ? styles.inspectorCollapsed : ''}`}><div className={styles.inspectorHeader}><h2>{selected?.kind === 'frame' ? 'Frame inspector' : 'Chart inspector'}</h2><button type="button" className={styles.inspectorToggle} onClick={() => setInspectorCollapsed((collapsed) => !collapsed)} aria-label={inspectorCollapsed ? 'Expand chart inspector' : 'Minimize chart inspector'} title={inspectorCollapsed ? 'Open chart options' : 'Minimize inspector'}>{inspectorCollapsed ? <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 5h14M3 10h14M3 15h14" /><circle cx="7" cy="5" r="1.5" /><circle cx="13" cy="10" r="1.5" /><circle cx="9" cy="15" r="1.5" /></svg> : '›'}</button></div>{!inspectorCollapsed && <>{selected?.kind === 'frame' && <><label>Frame name<input value={selected.name || ''} onChange={(event) => updateSelected({ name: event.target.value })} onKeyDown={(event) => event.stopPropagation()} /></label><p>{(selected.childIds || []).length} charts move together when you drag this frame. Deleting a frame leaves its charts in place.</p></>}{!selectedChart && selected?.kind !== 'frame' && <p>Select a chart to edit it.</p>}{selectedChart && <>
        <div className={styles.inspectorTabs} role="tablist" aria-label="Chart inspector sections"><button type="button" role="tab" aria-selected={inspectorTab === 'assistant'} onClick={() => setInspectorTab('assistant')}><BsChatDots aria-hidden="true" />Assistant</button><button type="button" role="tab" aria-selected={inspectorTab === 'map'} onClick={() => setInspectorTab('map')}><BsLink45Deg aria-hidden="true" />Map</button><button type="button" role="tab" aria-selected={inspectorTab === 'style'} onClick={() => setInspectorTab('style')}><BsBrush aria-hidden="true" />Style</button></div>
        {inspectorTab === 'assistant' && <section className={styles.assistantPlaceholder}><h3>Assistant</h3><p>Chart critique and guided recommendations will live here. For now, use the Map and Style tabs to build and compare your charts.</p></section>}
        {inspectorTab === 'map' && <>
        <label>Chart type<select value={selected.chartId} onChange={(event) => { const chart = selectableCanvasCharts.find((item) => item.metadata.id === event.target.value); if (!chart) return; const mapping = buildCompatibleChartMapping(chart, state.dataset.dataTypes, state.dataset.rows) || defaultMapping(chart, state.dataset.dataTypes, state.dataset.rows); const snapshot = createChartSnapshot({ chart, mapping, brandTheme: selected.brandTheme || DEFAULT_BRAND_THEME }); const retainedVisualOptions = getCompatibleChartVisualOptions(chart, selected.visualOptions); updateSelected({ ...snapshot, visualOptions: applyCanvasArtboard(chart, { ...snapshot.visualOptions, ...retainedVisualOptions }, selected.aspectRatio) }) }}>{selectableCanvasCharts.map((chart) => <option key={chart.metadata.id} value={chart.metadata.id}>{chart.metadata.name}</option>)}</select></label>
        {selectedChartNeedsNumericSize && <p className={styles.mappingNotice}>Bumpchart needs an inferred numeric field for Size. This dataset does not currently have one, so choose another chart or load data with a numeric measure.</p>}
        {getCanvasDimensions(selectedChart).map((dimension) => dimension.multiple
          ? <fieldset key={dimension.id} className={styles.multipleMapping}>
              <legend>{dimension.name}</legend>
              <p>{selectedChart.metadata.id === 'rawgraphs.forcedirectedgraph' && dimension.id === 'steps'
                ? 'Each selected field becomes a path step. Canvas connects adjacent steps in every row.'
                : `Each selected measure becomes a distinct color${selectedChart.metadata.id === 'rawgraphs.barchartstacked' ? ' stack' : 'ed bar'}.`}</p>
              <div>{getCompatibleDimensionColumns(dimension, state.dataset.dataTypes).map((column) => {
                const selectedColumns = selected.mapping?.[dimension.id]?.value || []
                const checked = selectedColumns.includes(column)
                return <label key={column} className={styles.mappingCheckbox}><input type="checkbox" checked={checked} disabled={checked && dimension.required && selectedColumns.length === 1} onChange={(event) => updateSelectedMultipleDimension(dimension, column, event.target.checked)} />{column}</label>
              })}</div>
            </fieldset>
          : <label key={dimension.id}>{dimension.name}<select value={selected.mapping?.[dimension.id]?.ids?.[0] || ''} onChange={(event) => updateSelected({ mapping: { ...selected.mapping, [dimension.id]: buildDimensionMappingForColumn(selected.mapping?.[dimension.id] || {}, dimension, event.target.value, state.dataset.dataTypes) } })}>{getCompatibleDimensionColumns(dimension, state.dataset.dataTypes).map((column) => <option key={column}>{column}</option>)}</select></label>
        )}
        {getCanvasDimensions(selectedChart).filter((dimension) => dimension.aggregation && !dimension.multiple).map((dimension) => <label key={`${dimension.id}-aggregation`}>Aggregation<select value={selected.mapping?.[dimension.id]?.config?.aggregation?.[0] || 'sum'} onChange={(event) => updateSelected({ mapping: { ...selected.mapping, [dimension.id]: { ...selected.mapping?.[dimension.id], config: { ...selected.mapping?.[dimension.id]?.config, aggregation: [event.target.value] } } } })}>{Object.entries(AGGREGATIONS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>)}
        {getCanvasDimensions(selectedChart).filter((dimension) => dimension.aggregation && dimension.multiple).map((dimension) => (selected.mapping?.[dimension.id]?.value || []).map((column, index) => <label key={`${dimension.id}-${column}-aggregation`}>{column} aggregation<select value={selected.mapping?.[dimension.id]?.config?.aggregation?.[index] || 'sum'} onChange={(event) => updateSelectedMultipleAggregation(dimension, column, event.target.value)}>{Object.entries(AGGREGATIONS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>))}
        {hasSeparateLegendMapping(selectedChart) && <label>{selectedChart.metadata.id === 'rawgraphs.barchart' ? 'Color bars by' : 'Color by'}<select value={selected.mapping?.[getLegendDimension(selectedChart).id]?.ids?.[0] || ''} onChange={(event) => updateSelectedLegendMapping(event.target.value)}><option value="">No color legend</option>{selectedLegendColumns.map((column) => <option key={column}>{column}</option>)}</select></label>}
        <button type="button" className={styles.autoMapButton} onClick={() => updateSelected({ mapping: defaultMapping(selectedChart, state.dataset.dataTypes, state.dataset.rows), visualOptions: { ...selected.visualOptions, showLegend: false } })}>Auto-map fields</button>
        </>}
        {inspectorTab === 'style' && <>
        <label>Chart title<CanvasChartTitleInput value={selected.title || selectedChart.metadata.name} onCommit={(title) => updateSelected({ title, brandTheme: { ...(selected.brandTheme || DEFAULT_BRAND_THEME), title } })} /></label>
        <CanvasVisualControls
          chart={selectedChart}
          mapping={selected.mapping}
          visualOptions={selected.visualOptions}
          onChange={(nextOptions) => updateChartVisualOptions(selected.id, nextOptions)}
          hasLegendData={Boolean(selected.mapping?.[getLegendDimension(selectedChart)?.id]?.value?.length)}
        />
        <CanvasLayoutDiagnostics diagnostics={selectedLayoutDiagnostics} />
        <CanvasLayoutActions
          brandTheme={selected.brandTheme || DEFAULT_BRAND_THEME}
          onBrandThemeChange={updateSelectedBrandTheme}
          marginSuggestions={selectedLayoutDiagnostics?.marginSuggestions}
          onVisualOptionsChange={(nextOptions) => updateChartVisualOptions(selected.id, nextOptions)}
          hasLegendData={Boolean(selected.mapping?.[getLegendDimension(selectedChart)?.id]?.value?.length)}
        />
        <CanvasStyleControls
          brandTheme={selected.brandTheme || DEFAULT_BRAND_THEME}
          onBrandThemeChange={updateSelectedBrandTheme}
          selectedChartCount={selectedChartIds.length}
          onApplyStyleToSelection={applySelectedStyleToSelection}
        />
        <label>Aspect ratio<select value={selected.aspectRatio} onChange={(event) => { const aspectRatio = event.target.value; updateSelected({ aspectRatio, h: selected.w / RATIOS[aspectRatio], visualOptions: applyCanvasArtboard(selectedChart, selected.visualOptions, aspectRatio) }) }}>{Object.keys(RATIOS).map((ratio) => <option key={ratio}>{ratio}</option>)}</select></label>{selected.parentVersionId && <p className={styles.lineage}>Duplicated from {state.objects.find((object) => object.id === selected.parentVersionId)?.title || 'an earlier chart version'}.</p>}<p className={styles.note}>Each duplicate owns its mapping and configuration. The canvas handles spatial comparison; precise production controls stay out of this prototype.</p>
        </>}
      </>}</>}</aside>
    </div>
  </main>
}
